import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { GripVertical, Eye, Star, RefreshCw, Trash2, AlertTriangle } from 'lucide-react';
import { useRanking, RankingItem } from '../../hooks/useRanking';
import { toast } from '../../hooks/use-toast';

interface RankingSidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const RankingSidebar: React.FC<RankingSidebarProps> = ({ isOpen, onClose }) => {
  const { 
    newArrivals, 
    topSelling, 
    loading, 
    error, 
    reorderRankings, 
    refreshData,
    cleanupExcessProducts,
    removeProductFromRanking
  } = useRanking();
  const [activeTab, setActiveTab] = useState<'new-arrivals' | 'top-selling'>('new-arrivals');

  const handleDragEnd = async (result: any, table: 'newariverank' | 'topsellrank') => {
    if (!result.destination) return;

    const items = table === 'newariverank' ? newArrivals : topSelling;
    const reorderedItems = Array.from(items);
    const [reorderedItem] = reorderedItems.splice(result.source.index, 1);
    reorderedItems.splice(result.destination.index, 0, reorderedItem);

    try {
      await reorderRankings(table, reorderedItems);
      toast({
        title: "Ranking Updated",
        description: `Product rankings have been successfully reordered.`,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update rankings. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleCleanupExcess = async (table: 'newariverank' | 'topsellrank') => {
    try {
      await cleanupExcessProducts(table);
      toast({
        title: "Cleanup Complete",
        description: `Removed excess products from ${table === 'newariverank' ? 'New Arrivals' : 'Top Selling'} ranking.`,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to cleanup excess products. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleRemoveProduct = async (table: 'newariverank' | 'topsellrank', productId: string, productName: string) => {
    if (!confirm(`Are you sure you want to remove "${productName}" from the ranking?`)) {
      return;
    }

    try {
      await removeProductFromRanking(table, productId);
      toast({
        title: "Product Removed",
        description: `"${productName}" has been removed from the ranking.`,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to remove product. Please try again.",
        variant: "destructive",
      });
    }
  };

  const renderProductCard = (item: RankingItem, index: number, table: 'newariverank' | 'topsellrank') => (
    <Draggable key={item.id} draggableId={item.id} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          className={`mb-2 p-3 border rounded-lg bg-white shadow-sm transition-all ${
            snapshot.isDragging ? 'shadow-lg scale-105' : ''
          }`}
        >
          <div className="flex items-center space-x-3">
            <div
              {...provided.dragHandleProps}
              className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600"
            >
              <GripVertical size={16} />
            </div>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-2 mb-1">
                <Badge variant="secondary" className="text-xs">
                  #{item.rank}
                </Badge>
                <div className="flex items-center space-x-1">
                  <Star size={12} className="text-yellow-500 fill-current" />
                  <span className="text-xs text-gray-500">4.5</span>
                </div>
              </div>
              
              <h4 className="text-sm font-medium text-gray-900 truncate">
                {item.product_name}
              </h4>
              
              <div className="flex items-center justify-between mt-1">
                <span className="text-sm font-semibold text-green-600">
                  ₹{item.product_price?.toLocaleString()}
                </span>
                <div className="flex items-center space-x-1 text-gray-400">
                  <Eye size={12} />
                  <span className="text-xs">0</span>
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              {item.product_image && (
                <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                  <img
                    src={item.product_image}
                    alt={item.product_name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                </div>
              )}
              
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleRemoveProduct(table, item.product_id, item.product_name || 'Unknown Product')}
                className="text-red-500 hover:text-red-700 hover:bg-red-50 p-1"
              >
                <Trash2 size={14} />
              </Button>
            </div>
          </div>
        </div>
      )}
    </Draggable>
  );

  const renderRankingList = (items: RankingItem[], table: 'newariverank' | 'topsellrank') => {
    const isAtLimit = items.length >= 10;
    const tableName = table === 'newariverank' ? 'New Arrivals' : 'Top Selling';
    
    return (
      <div className="space-y-3">
        {/* Limit Warning */}
        {isAtLimit && (
          <div className="flex items-center space-x-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <AlertTriangle size={16} className="text-yellow-600" />
            <div className="text-sm text-yellow-800">
              <strong>Limit Reached:</strong> Maximum 10 products allowed. Remove a product to add new ones.
            </div>
          </div>
        )}
        
        {/* Cleanup Button */}
        {items.length > 10 && (
          <div className="flex justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleCleanupExcess(table)}
              className="text-red-600 border-red-200 hover:bg-red-50"
            >
              <Trash2 size={14} className="mr-1" />
              Cleanup Excess ({items.length - 10} extra)
            </Button>
          </div>
        )}
        
        <DragDropContext onDragEnd={(result) => handleDragEnd(result, table)}>
          <Droppable droppableId={`${table}-list`}>
            {(provided) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                className="space-y-2 max-h-96 overflow-y-auto"
              >
                {items.map((item, index) => renderProductCard(item, index, table))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose} />
      <div className="absolute right-0 top-0 h-full w-96 bg-white shadow-xl">
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b">
            <h2 className="text-lg font-semibold text-gray-900">Ranking Management</h2>
            <div className="flex items-center space-x-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={refreshData}
                disabled={loading}
              >
                <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
              </Button>
              <Button variant="ghost" size="sm" onClick={onClose}>
                ×
              </Button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-hidden">
            <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as any)} className="h-full">
              <TabsList className="grid w-full grid-cols-2 m-4">
                <TabsTrigger value="new-arrivals">New Arrivals</TabsTrigger>
                <TabsTrigger value="top-selling">Top Selling</TabsTrigger>
              </TabsList>

              <div className="px-4 pb-4 flex-1 overflow-hidden">
                <TabsContent value="new-arrivals" className="h-full">
                  <Card className="h-full">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-medium text-gray-700">
                        New Arrivals Ranking ({newArrivals.length}/10 products)
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="h-full overflow-hidden">
                      {loading ? (
                        <div className="flex items-center justify-center h-32">
                          <RefreshCw className="animate-spin" size={24} />
                        </div>
                      ) : error ? (
                        <div className="text-center text-red-500 py-4">
                          <p>Error: {error}</p>
                        </div>
                      ) : (
                        renderRankingList(newArrivals, 'newariverank')
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="top-selling" className="h-full">
                  <Card className="h-full">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-medium text-gray-700">
                        Top Selling Ranking ({topSelling.length}/10 products)
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="h-full overflow-hidden">
                      {loading ? (
                        <div className="flex items-center justify-center h-32">
                          <RefreshCw className="animate-spin" size={24} />
                        </div>
                      ) : error ? (
                        <div className="text-center text-red-500 py-4">
                          <p>Error: {error}</p>
                        </div>
                      ) : (
                        renderRankingList(topSelling, 'topsellrank')
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>
              </div>
            </Tabs>
          </div>

          {/* Footer */}
          <div className="p-4 border-t bg-gray-50">
            <div className="text-xs text-gray-500 text-center">
              Drag and drop to reorder products. Changes are saved automatically.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RankingSidebar;
